'use client';
// Send — AMAN and AMT-20 token transfers from the active session wallet, plus the Testnet faucet.
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Send as SendIcon, Loader2, Droplets, Coins, ArrowLeftRight, Info, Wallet as WalletIcon,
} from 'lucide-react';
import { fetchJson, usePoll, post, fmtAmt, SectionHeader, Mono, TokenAvatar } from './shared';
import { useSession, ConnectPrompt } from './session';
import type { Net, Tx, TokensData } from './types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface MempoolData { mempool: Tx[] }

export function Send({ net }: { net: Net }) {
  const { session, wallet, openAccount } = useSession();
  const { data: statsData } = usePoll<{ network: { faucet: boolean } }>(
    async () => fetchJson(`/api/stats?net=${net}`), 8000, [net]);
  const { data: mempoolData } = usePoll<MempoolData>(
    async () => fetchJson(`/api/tx?net=${net}`), 4000, [net]);
  const { data: tokensData } = usePoll<TokensData>(
    async () => fetchJson(`/api/tokens?net=${net}`), 10000, [net]);
  const logoMap = new Map((tokensData?.tokens ?? []).map(t => [t.symbol, t.logo]));

  const faucetOn = statsData?.network?.faucet === true;

  if (!session) {
    return (
      <div className="space-y-5">
        <SectionHeader
          icon={SendIcon}
          title="Send assets"
          desc="Transfers are signed twice (Ed25519 + ML-DSA-87) in your wallet, queued in the mempool, then mined into a block"
        />
        <ConnectPrompt what="send assets" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <SectionHeader
        icon={SendIcon}
        title="Send assets"
        desc="Transfers are signed twice (Ed25519 + ML-DSA-87), queued in the mempool, then mined into a block — from your wallet only"
      />

      <Tabs defaultValue="aman">
        <TabsList className="grid grid-cols-2 w-full max-w-md h-10">
          <TabsTrigger value="aman" className="gap-1.5 text-xs font-bold"><ArrowLeftRight className="size-3.5" /> AMAN</TabsTrigger>
          <TabsTrigger value="token" className="gap-1.5 text-xs font-bold"><Coins className="size-3.5" /> AMT-20 Token</TabsTrigger>
        </TabsList>
        <TabsContent value="aman" className="mt-4">
          <SendAman net={net} session={session} balance={wallet?.balance ?? 0} faucetOn={faucetOn} />
        </TabsContent>
        <TabsContent value="token" className="mt-4">
          <SendToken net={net} session={session} assets={wallet?.assets ?? []} logoMap={logoMap} />
        </TabsContent>
      </Tabs>

      {/* Mempool */}
      <div className="glass p-5">
        <SectionHeader icon={Loader2} title="Pending transactions (mempool)" desc="Awaiting confirmation by the next block" />
        <div className="space-y-2 max-h-72 overflow-y-auto pl-1">
          {(mempoolData?.mempool ?? []).map(t => (
            <div key={t.id} className="rounded-lg border border-border/60 bg-muted/30 p-3 flex items-center justify-between gap-3">
              <span className="text-xs font-bold">{t.label ?? t.type}</span>
              <Mono className="truncate">{t.id.slice(0, 22)}…</Mono>
              <span className="text-xs font-black text-emerald-400" dir="ltr">{fmtAmt(t.amount)} {t.type.startsWith('token') ? t.data?.symbol : 'AMAN'}</span>
            </div>
          ))}
          {(mempoolData?.mempool ?? []).length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-4">The mempool is empty — everything is confirmed.</div>
          )}
        </div>
      </div>

      {/* Open account shortcut hidden — kept for completeness */}
      <button onClick={openAccount} className="sr-only">Open account</button>
    </div>
  );
}

// ---------- AMAN transfer ----------
function SendAman({ net, session, balance, faucetOn }: {
  net: Net; session: { walletId: string; address: string; name: string; password: string };
  balance: number; faucetOn: boolean;
}) {
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const amt = Number(amount || 0);
  const canSend = amt > 0 && amt + 0.001 <= balance && /^AM[0-9a-f]{48}$/.test(to.trim());

  const send = async () => {
    setBusy(true);
    try {
      const r = await post<{ id?: string; error?: string; message?: string }>(`/api/tx?net=${net}`, {
        walletId: session.walletId, password: session.password, type: 'transfer',
        to: to.trim(), amount: amt, fee: 0.001,
      });
      if (r.error) { toast.error(r.error); return; }
      toast.success('Transfer queued', { description: r.message ?? 'Waiting to be mined.' });
      setAmount(''); setTo('');
    } finally {
      setBusy(false);
    }
  };

  const claimFaucet = async () => {
    try {
      const r = await post<{ id?: string; error?: string; message?: string }>('/api/faucet?net=testnet', { address: session.address });
      if (r.error) { toast.error(r.error); return; }
      toast.success('Faucet request queued', { description: r.message });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Faucet failed');
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 glass p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold mb-1.5 block">From — your session wallet</label>
            <div className="rounded-lg bg-muted/40 ring-1 ring-border px-3 py-2.5 text-sm font-bold flex items-center gap-2">
              <WalletIcon className="size-4 text-primary" /> {session.name}
              <span className="text-muted-foreground font-normal text-xs">· {fmtAmt(balance, 4)} AMAN</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold mb-1.5 block" htmlFor="sa-amount">Amount (fee 0.001 AMAN)</label>
            <div className="flex gap-2">
              <Input id="sa-amount" type="number" min={0} value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.0" className="text-sm" />
              <Button variant="secondary" size="sm" className="text-xs shrink-0 ring-1 ring-border" onClick={() => setAmount(String(Math.max(0, balance - 0.001)))}>
                MAX
              </Button>
            </div>
          </div>
        </div>
        <div>
          <label className="text-xs font-bold mb-1.5 block" htmlFor="sa-to">Recipient address</label>
          <Input id="sa-to" value={to} onChange={e => setTo(e.target.value)} placeholder="AM…" className="mono text-sm" />
        </div>
        <div className="text-[0.7rem] text-muted-foreground flex items-center gap-1.5">
          <WalletIcon className="size-3.5" /> Available after sending: <b className="text-foreground" dir="ltr">{fmtAmt(Math.max(0, balance - amt - 0.001), 4)} AMAN</b>
        </div>
        <Button onClick={send} disabled={!canSend || busy} className="w-full font-black gap-2">
          {busy ? <Loader2 className="size-4 animate-spin" /> : <SendIcon className="size-4" />}
          {busy ? 'Signing twice…' : 'Sign & send'}
        </Button>
      </div>

      <div className="space-y-3">
        {faucetOn && (
          <div className="glass p-4">
            <div className="flex items-center gap-2 text-sm font-bold mb-1.5"><Droplets className="size-4 text-sky-400" /> Testnet faucet</div>
            <p className="text-[0.7rem] text-muted-foreground leading-relaxed">Request 250 test AMAN per minute — test coins carry zero value and exist for rehearsal only.</p>
            <Button onClick={claimFaucet} disabled={busy} variant="secondary" size="sm" className="w-full mt-3 gap-1.5 text-xs ring-1 ring-sky-500/30 text-sky-300">
              <Droplets className="size-3.5" /> Claim 250 AMAN
            </Button>
          </div>
        )}
        <div className="glass p-4">
          <div className="flex items-center gap-2 text-sm font-bold mb-2"><Info className="size-4 text-cyan-400" /> How it works</div>
          <ul className="text-[0.7rem] text-muted-foreground space-y-1.5 leading-relaxed list-disc pl-4">
            <li>The node builds the canonical message and requires <b className="text-foreground">both</b> signatures.</li>
            <li>50% of the fee is burned — permanently removed from supply.</li>
            <li>The Chain-ID inside the message blocks replaying this transfer on the other network.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---------- Token transfer ----------
function SendToken({ net, session, assets, logoMap }: {
  net: Net; session: { walletId: string; password: string };
  assets: Array<{ symbol: string; balance: number }>;
  logoMap: Map<string, string | undefined>;
}) {
  const [symbol, setSymbol] = useState('');
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const holding = assets.find(a => a.symbol === symbol)?.balance ?? 0;
  const amt = Number(amount || 0);
  const canSend = Boolean(symbol) && amt > 0 && amt <= holding && /^AM[0-9a-f]{48}$/.test(to.trim());

  const send = async () => {
    setBusy(true);
    try {
      const r = await post<{ id?: string; error?: string; message?: string }>(`/api/tx?net=${net}`, {
        walletId: session.walletId, password: session.password, type: 'token_transfer',
        symbol, to: to.trim(), amount: amt,
      });
      if (r.error) { toast.error(r.error); return; }
      toast.success('Token transfer queued', { description: r.message ?? 'Waiting to be mined.' });
      setAmount(''); setTo('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass p-5 space-y-4">
      {assets.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-6">
          Your wallet holds no tokens yet. Buy on the DEX, deploy one in the Token Lab, or receive a transfer.
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-bold mb-1.5 flex items-center gap-2" htmlFor="st-sym">
                Token {symbol && <TokenAvatar symbol={symbol} logo={logoMap.get(symbol)} size={18} />}
              </label>
              <select id="st-sym" value={symbol} onChange={e => setSymbol(e.target.value)}
                className="w-full rounded-lg bg-background/60 ring-1 ring-input px-3 py-2.5 text-sm outline-none focus:ring-primary/60">
                <option value="">Select a token…</option>
                {assets.map(a => <option key={a.symbol} value={a.symbol}>{a.symbol} — {fmtAmt(a.balance, 2)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold mb-1.5 block" htmlFor="st-amount">Amount (fee 0.001 AMAN)</label>
              <div className="flex gap-2">
                <Input id="st-amount" type="number" min={0} value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.0" className="text-sm" />
                {holding > 0 && (
                  <Button variant="secondary" size="sm" className="text-xs shrink-0 ring-1 ring-border" onClick={() => setAmount(String(holding))}>MAX</Button>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold mb-1.5 block">Holding</label>
              <div className="rounded-lg bg-muted/40 ring-1 ring-border px-3 py-2.5 text-sm font-black" dir="ltr">
                {fmtAmt(holding)} {symbol || '—'}
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold mb-1.5 block" htmlFor="st-to">Recipient address</label>
            <Input id="st-to" value={to} onChange={e => setTo(e.target.value)} placeholder="AM…" className="mono text-sm" />
          </div>
          <Button onClick={send} disabled={!canSend || busy} className="w-full font-black gap-2">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Coins className="size-4" />}
            {busy ? 'Signing twice…' : `Send ${symbol || 'token'}`}
          </Button>
        </>
      )}
    </div>
  );
}
